import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { DashboardApi } from '../../../services'
import { WaterTestStripState } from '../waterTestStrip.slice'
import { RootState } from '../../store';
import { StorageUtils } from '../../Verification';

interface UploadResponse {
  jobId: string;
  status: 'processing' | 'completed' | 'failed';
}

interface AnalysisResponse {
  status: 'processing' | 'completed' | 'failed';
  result?: any;
}

export const uploadAndAnalyzeImageAsync = createAsyncThunk(
  'waterTestStrip/uploadAndAnalyze',
  async (file: File, {getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { verifiedEmail, isVerified } = state.verification;
      const savedState = StorageUtils.getVerificationState();
      const email = verifiedEmail || savedState?.email;
      
      if (!isVerified || !savedState?.isVerified) {
        throw new Error('Verification required');
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('email', email); 
      const uploadResponse = await DashboardApi.uploadWaterTestStrip(formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const uploadData = (uploadResponse as AxiosResponse<UploadResponse>).data;
      if (!uploadData.jobId) {
        throw new Error('No job ID received');
      }
      let result = null;
      while (true) {
        const pollResponse = await DashboardApi.getWaterTestStripResult(uploadData.jobId);
        const pollData = (pollResponse as AxiosResponse<AnalysisResponse>).data;
        if (pollData.status === 'completed') {
          result = pollData.result;
          break;
        } else if (pollData.status === 'failed') {
          throw new Error('Analysis failed');
        }
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
      return { result, jobId: uploadData.jobId };
    } catch (error: any) {
      console.error(error);
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const uploadAndAnalyzeImageAsyncBuilder = (
  builder: ActionReducerMapBuilder<WaterTestStripState>
) => {
  builder.addCase(uploadAndAnalyzeImageAsync.pending, (state) => {
    state.loading = true;
    state.status = 'uploading';
    state.success = false;
    state.error = null;
  });

  builder.addCase(
    uploadAndAnalyzeImageAsync.fulfilled,
    (state, action: PayloadAction<{ result: any; jobId: string }>) => {
      state.loading = false;
      state.success = true;
      state.status = 'completed';
      state.data = action.payload.result;
      }
  );

  builder.addCase(
    uploadAndAnalyzeImageAsync.rejected,
    (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.success = false;
      state.status = 'failed';
      state.error = action.payload;
    }
  );
};